import { supabase } from './supabaseClient';
import type { IdentityDocumentSummary, IdentityDocumentStatus } from '../modules/identity/types';

const GATEWAY_BASE_URL = import.meta.env.VITE_IDENTITY_GATEWAY_BASE_URL;

// ── Diagnostic capture ─────────────────────────────────

export interface IdentityGatewayDiagnostic {
  timestamp: string;
  method: string;
  path: string;
  status: 'success' | 'error';
  statusCode?: number;
  /** Categorized error type for quick triage */
  errorType?: 'cors' | 'network' | 'auth' | 'gateway_4xx' | 'gateway_5xx' | 'unknown';
  errorMessage?: string;
  /** Origin that made the request (for CORS debugging) */
  requestOrigin?: string;
  customerId?: string;
  orderId?: string;
  /** Extra response metadata (no secrets, no signed URLs) */
  extra?: Record<string, unknown>;
}

let diagnosticHistory: IdentityGatewayDiagnostic[] = [];

/** In-memory cache for customer identity status to avoid repeated calls */
const identityStatusCache = new Map<string, { data: IdentityDocumentSummary; ts: number }>();
const CACHE_TTL_MS = 30000; // 30 seconds

export function getIdentityGatewayDiagnostics(): IdentityGatewayDiagnostic[] {
  return [...diagnosticHistory];
}

export function clearIdentityGatewayDiagnostics(): void {
  diagnosticHistory = [];
}

export function clearIdentityStatusCache(): void {
  identityStatusCache.clear();
}

function pushDiagnostic(entry: IdentityGatewayDiagnostic) {
  diagnosticHistory.unshift(entry);
  if (diagnosticHistory.length > 20) {
    diagnosticHistory.pop();
  }
}

function classifyError(err: unknown, statusCode?: number): IdentityGatewayDiagnostic['errorType'] {
  const msg = String(err);
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('Network request failed')) {
    return 'cors';
  }
  if (statusCode && statusCode >= 400 && statusCode < 500) return 'gateway_4xx';
  if (statusCode && statusCode >= 500) return 'gateway_5xx';
  if (msg.includes('Auth') || msg.includes('token') || msg.includes('登入')) return 'auth';
  return 'unknown';
}

function sanitizeErrorMessage(msg: string): string {
  return msg
    .replace(/Bearer\s+[A-Za-z0-9\-_]+\.?[A-Za-z0-9\-_]*\.?[A-Za-z0-9\-_]*/gi, 'Bearer [REDACTED]')
    .replace(/access_token["']?\s*[:=]\s*["'][^"']+["']/gi, 'access_token: [REDACTED]')
    .replace(/apikey["']?\s*[:=]\s*["'][^"']+["']/gi, 'apikey: [REDACTED]');
}

/** Check if a customerId looks like a mock/demo UUID that should skip gateway checks */
export function isMockCustomerId(customerId: string): boolean {
  if (!customerId) return true;
  // Patterns like 00000000-0000-0000-0000-000000000000 or 00000000-0000-0000-0000-000000000001
  return /^00000000-0000-0000-0000-00000000000[0-9a-f]$/.test(customerId.toLowerCase());
}

async function getAuthToken(): Promise<string | null> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) return null;
  return data.session.access_token;
}

async function gatewayFetch<T>(
  path: string,
  options: {
    method?: string;
    body?: string;
    headers?: Record<string, string>;
    meta?: { customerId?: string; orderId?: string };
    extractMeta?: (data: T) => Record<string, unknown>;
  } = {}
): Promise<T> {
  const token = await getAuthToken();
  if (!token) {
    pushDiagnostic({
      timestamp: new Date().toISOString(),
      method: options.method || 'GET',
      path,
      status: 'error',
      errorType: 'auth',
      errorMessage: 'Auth token missing',
      requestOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
      customerId: options.meta?.customerId,
      orderId: options.meta?.orderId,
    });
    throw new Error('未登入或無法取得身份驗證令牌');
  }

  const url = `${GATEWAY_BASE_URL}${path}`;
  const requestOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;
  let resp: Response | null = null;
  try {
    resp = await fetch(url, {
      method: options.method || 'GET',
      body: options.body,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  } catch (networkErr) {
    const errMsg = String(networkErr);
    const errorType = classifyError(networkErr);
    pushDiagnostic({
      timestamp: new Date().toISOString(),
      method: options.method || 'GET',
      path,
      status: 'error',
      errorType,
      errorMessage: sanitizeErrorMessage(errMsg),
      requestOrigin,
      customerId: options.meta?.customerId,
      orderId: options.meta?.orderId,
    });
    throw networkErr;
  }

  if (!resp.ok) {
    let message = `Gateway 錯誤 (${resp.status})`;
    try {
      const body = await resp.json();
      message = body?.message || body?.error || message;
    } catch {
      /* ignore */ }
    pushDiagnostic({
      timestamp: new Date().toISOString(),
      method: options.method || 'GET',
      path,
      status: 'error',
      errorType: classifyError(null, resp.status),
      statusCode: resp.status,
      errorMessage: sanitizeErrorMessage(message),
      requestOrigin,
      customerId: options.meta?.customerId,
      orderId: options.meta?.orderId,
    });
    throw new Error(message);
  }

  // Parse response before pushing diagnostic so we can extract metadata
  let data: T;
  const contentType = resp.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    data = (await resp.json()) as T;
  } else {
    data = (await resp.text()) as unknown as T;
  }

  pushDiagnostic({
    timestamp: new Date().toISOString(),
    method: options.method || 'GET',
    path,
    status: 'success',
    statusCode: resp.status,
    requestOrigin,
    customerId: options.meta?.customerId,
    orderId: options.meta?.orderId,
    extra: options.extractMeta ? options.extractMeta(data) : undefined,
  });

  return data;
}

// ── Concurrency limiter ─────────────────────────────────

interface ConcurrencyTask<T> {
  fn: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (reason: unknown) => void;
}

class ConcurrencyLimiter {
  private running = 0;
  private queue: ConcurrencyTask<unknown>[] = [];

  constructor(private maxConcurrency: number) {}

  async run<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push({ fn: fn as () => Promise<unknown>, resolve: resolve as (value: unknown) => void, reject });
      this.pump();
    }) as Promise<T>;
  }

  private pump() {
    while (this.running < this.maxConcurrency && this.queue.length > 0) {
      const task = this.queue.shift()!;
      this.running++;
      task
        .fn()
        .then(task.resolve)
        .catch(task.reject)
        .finally(() => {
          this.running--;
          this.pump();
        });
    }
  }
}

/** Shared concurrency limiter for identity gateway calls (max 3 parallel) */
const identityGatewayLimiter = new ConcurrencyLimiter(3);

// ── 型別 ──────────────────────────────────────────────

/** Gateway 原始回應（snake_case） */
export interface GatewayIdentityStatusResponse {
  overall_status: string;
  identity_ready: boolean;
  front_uploaded: boolean;
  back_uploaded: boolean;
  latest_document_id?: string | null;
  verification_status?: string | null;
  precheck_status?: string | null;
}

function safeStatus(s: string): IdentityDocumentStatus {
  const valid: IdentityDocumentStatus[] = ['not_uploaded', 'pending', 'verified', 'rejected', 'masked', 'destroyed'];
  return valid.includes(s as IdentityDocumentStatus) ? (s as IdentityDocumentStatus) : 'not_uploaded';
}

/** 將 Gateway snake_case 回應轉換為前端 camelCase IdentityDocumentSummary */
export function mapGatewayStatusToSummary(response: GatewayIdentityStatusResponse): IdentityDocumentSummary {
  const overallStatus = safeStatus(response.overall_status);

  const deriveSideStatus = (uploaded: boolean): IdentityDocumentStatus => {
    if (!uploaded) return 'not_uploaded';
    if (overallStatus === 'not_uploaded') return 'pending';
    return overallStatus;
  };

  return {
    overallStatus,
    frontStatus: deriveSideStatus(response.front_uploaded),
    backStatus: deriveSideStatus(response.back_uploaded),
    lastUploadedAt: null,
    lastVerifiedAt: overallStatus === 'verified' ? new Date().toISOString() : null,
    frontDocumentId: response.latest_document_id || undefined,
    backDocumentId: response.latest_document_id || undefined,
    identityReady: response.identity_ready === true,
    frontUploaded: response.front_uploaded === true,
    backUploaded: response.back_uploaded === true,
    source: 'gateway',
  };
}

export interface UploadSessionResponse {
  sessionId: string;
  uploadUrl: string;
  fields?: Record<string, string>;
  expiresAt: string;
}

export interface UploadCompleteRequest {
  sessionId: string;
  customerId: string;
  documentType: 'front' | 'back';
  fileName: string;
  fileSize: number;
  mimeType: string;
}

export interface UploadCompleteResponse {
  documentId: string;
  status: string;
}

export interface VerifyRequest {
  documentId: string;
  verifierId: string;
  verifierName: string;
}

export interface RejectRequest {
  documentId: string;
  reason: string;
  verifierId: string;
  verifierName: string;
}

export interface ViewUrlResponse {
  signedUrl: string;
  expiresAt: string;
}

export interface ContractIdentityAssetsResponse {
  front?: {
    documentId: string;
    signedUrl: string;
    expiresAt: string;
  };
  back?: {
    documentId: string;
    signedUrl: string;
    expiresAt: string;
  };
  verified: boolean;
}

/** Raw gateway response — supports multiple schema shapes */
interface RawContractAssetsResponse {
  assets?: {
    front?: { signed_url?: string; signedUrl?: string; document_id?: string; documentId?: string; expires_at?: string; expiresAt?: string };
    back?: { signed_url?: string; signedUrl?: string; document_id?: string; documentId?: string; expires_at?: string; expiresAt?: string };
  };
  front?: { signed_url?: string; signedUrl?: string; document_id?: string; documentId?: string; expires_at?: string; expiresAt?: string };
  back?: { signed_url?: string; signedUrl?: string; document_id?: string; documentId?: string; expires_at?: string; expiresAt?: string };
  front_url?: string;
  frontUrl?: string;
  back_url?: string;
  backUrl?: string;
  verified?: boolean;
}

/** Normalize various gateway response schemas to frontend standard ContractIdentityAssetsResponse */
function normalizeContractAssetsResponse(raw: RawContractAssetsResponse): ContractIdentityAssetsResponse {
  const getSignedUrl = (obj: { signed_url?: string; signedUrl?: string } | undefined): string | undefined =>
    obj?.signed_url || obj?.signedUrl || undefined;
  const getDocId = (obj: { document_id?: string; documentId?: string } | undefined): string | undefined =>
    obj?.document_id || obj?.documentId || undefined;
  const getExpires = (obj: { expires_at?: string; expiresAt?: string } | undefined): string | undefined =>
    obj?.expires_at || obj?.expiresAt || undefined;

  const frontSignedUrl = getSignedUrl(raw.assets?.front) || getSignedUrl(raw.front) || raw.front_url || raw.frontUrl;
  const backSignedUrl = getSignedUrl(raw.assets?.back) || getSignedUrl(raw.back) || raw.back_url || raw.backUrl;

  const frontDocId = getDocId(raw.assets?.front) || getDocId(raw.front);
  const backDocId = getDocId(raw.assets?.back) || getDocId(raw.back);

  const frontExpires = getExpires(raw.assets?.front) || getExpires(raw.front);
  const backExpires = getExpires(raw.assets?.back) || getExpires(raw.back);

  return {
    front: frontSignedUrl
      ? { documentId: frontDocId || '', signedUrl: frontSignedUrl, expiresAt: frontExpires || '' }
      : undefined,
    back: backSignedUrl
      ? { documentId: backDocId || '', signedUrl: backSignedUrl, expiresAt: backExpires || '' }
      : undefined,
    verified: raw.verified === true,
  };
}

/** Collect non-sensitive response key names for diagnostics (no values, no URLs) */
function collectResponseKeys(raw: RawContractAssetsResponse): string[] {
  const keys: string[] = [];
  if (raw.assets) {
    keys.push('assets');
    if (raw.assets.front) keys.push('assets.front');
    if (raw.assets.back) keys.push('assets.back');
  }
  if (raw.front) keys.push('front');
  if (raw.back) keys.push('back');
  if (raw.front_url !== undefined || raw.frontUrl !== undefined) keys.push('front_url/frontUrl');
  if (raw.back_url !== undefined || raw.backUrl !== undefined) keys.push('back_url/backUrl');
  if (raw.verified !== undefined) keys.push('verified');
  return keys;
}

// ── API ───────────────────────────────────────────────

/** GET /identity/me/status */
export async function getMyIdentityStatus(): Promise<IdentityDocumentSummary> {
  const raw = await gatewayFetch<GatewayIdentityStatusResponse>('/identity/me/status');
  return mapGatewayStatusToSummary(raw);
}

/** GET /identity/customer/:customerId/status (with cache + concurrency + mock skip) */
export async function getCustomerIdentityStatus(
  customerId: string
): Promise<IdentityDocumentSummary> {
  // Skip mock/demo customer IDs to avoid polluting diagnostics
  if (isMockCustomerId(customerId)) {
    return mapGatewayStatusToSummary({
      overall_status: 'not_uploaded',
      identity_ready: false,
      front_uploaded: false,
      back_uploaded: false,
    });
  }

  // Check cache first
  const cached = identityStatusCache.get(customerId);
  if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
    return cached.data;
  }

  const raw = await identityGatewayLimiter.run(() =>
    gatewayFetch<GatewayIdentityStatusResponse>(
      `/identity/customer/${encodeURIComponent(customerId)}/status`,
      { meta: { customerId } }
    )
  );

  const summary = mapGatewayStatusToSummary(raw);
  identityStatusCache.set(customerId, { data: summary, ts: Date.now() });
  return summary;
}

/** POST /identity/upload-session */
export async function requestIdentityUploadSession(body: {
  customerId: string;
  documentType: 'front' | 'back';
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: 'customer_self' | 'staff_assist';
  uploaderId?: string;
}): Promise<UploadSessionResponse> {
  return gatewayFetch<UploadSessionResponse>('/identity/upload-session', {
    method: 'POST',
    body: JSON.stringify(body),
    meta: { customerId: body.customerId },
  });
}

/** POST /identity/upload-complete */
export async function completeIdentityUpload(
  body: UploadCompleteRequest
): Promise<UploadCompleteResponse> {
  return gatewayFetch<UploadCompleteResponse>('/identity/upload-complete', {
    method: 'POST',
    body: JSON.stringify(body),
    meta: { customerId: body.customerId },
  });
}

/** POST /identity/verify */
export async function verifyIdentityDocument(body: VerifyRequest): Promise<{ success: boolean; documentId: string }> {
  return gatewayFetch<{ success: boolean; documentId: string }>('/identity/verify', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** POST /identity/reject */
export async function rejectIdentityDocument(body: RejectRequest): Promise<{ success: boolean; documentId: string }> {
  return gatewayFetch<{ success: boolean; documentId: string }>('/identity/reject', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** GET /identity/:id/view-url?reason=...&ttl=... */
export async function getIdentityViewUrl(
  documentId: string,
  reason: string,
  ttlSeconds = 300
): Promise<ViewUrlResponse> {
  const qs = new URLSearchParams({ reason, ttl: String(ttlSeconds) });
  return gatewayFetch<ViewUrlResponse>(`/identity/${encodeURIComponent(documentId)}/view-url?${qs.toString()}`);
}

/** POST /contract/:orderId/identity-assets */
export async function getContractIdentityAssets(
  orderId: string,
  params: {
    customer_id: string;
    identity_document_id: string;
    order_status: string;
  }
): Promise<ContractIdentityAssetsResponse> {
  const idempotencyKey = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  const raw = await gatewayFetch<RawContractAssetsResponse>(
    `/contract/${encodeURIComponent(orderId)}/identity-assets`,
    {
      method: 'POST',
      headers: {
        'Idempotency-Key': idempotencyKey,
      },
      body: JSON.stringify({
        customer_id: params.customer_id,
        identity_document_id: params.identity_document_id,
        order_status: params.order_status,
        purpose: 'contract_print',
      }),
      meta: { orderId, customerId: params.customer_id },
      extractMeta: (data) => {
        const normalized = normalizeContractAssetsResponse(data);
        return {
          frontUrlExists: !!normalized.front?.signedUrl,
          backUrlExists: !!normalized.back?.signedUrl,
          responseKeys: collectResponseKeys(data),
        };
      },
    }
  );

  return normalizeContractAssetsResponse(raw);
}

/** 檢查 gateway 是否可用（健康檢查） */
export async function checkIdentityGatewayHealth(): Promise<boolean> {
  try {
    const token = await getAuthToken();
    if (!token) return false;
    const resp = await fetch(`${GATEWAY_BASE_URL}/health`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return resp.ok;
  } catch {
    return false;
  }
}